import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ShoppingCart, ClipboardList, WifiOff } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { useOfflineQueue } from '@/hooks/use-offline-queue';
import { formatUZS } from '@/lib/format';

export default function POSLayout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { cart, cartTotal } = useApp();
  const { isOnline, queuedCount } = useOfflineQueue();

  return (
    <div className="flex flex-col h-screen bg-pos-surface">
      <header className="flex items-center justify-between h-14 px-4 bg-card border-b border-border">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="p-2 rounded-lg hover:bg-accent text-muted-foreground">
            <ArrowLeft size={18} />
          </Link>
          <span className="font-semibold text-foreground text-sm">{t('misc_nav.pos')}</span>
        </div>
        <div className="flex items-center gap-3">
          {(!isOnline || queuedCount > 0) && (
            <span
              className="flex items-center gap-1 px-2 py-1 rounded-full bg-warning/10 text-warning text-[10px] font-medium"
              title={!isOnline ? t('misc_nav.offline_title') : t('misc_nav.queued_title')}
            >
              <WifiOff size={10} />
              {!isOnline ? t('misc_nav.offline') : t('misc_nav.queued', { count: queuedCount })}
            </span>
          )}
          <Link to="/pos/shift" className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-accent text-sm text-muted-foreground">
            <ClipboardList size={16} />
            <span className="hidden sm:inline">{t('misc_nav.shift')}</span>
          </Link>
          <Link to="/pos/cart" className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm">
            <ShoppingCart size={16} />
            <span>{t('misc_nav.cart_count', { count: cart.length })}</span>
            <span className="font-medium">{formatUZS(cartTotal)}</span>
          </Link>
          <span className="text-xs text-muted-foreground">{user?.full_name}</span>
        </div>
      </header>
      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
}
